import React, { memo, useEffect, useState } from 'react';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import { ONE_MINUTE_IN_MILLISECONDS as ONE_MINUTE_INTERVAL } from '../../constants/duration';
import { DateFormat } from '../../types/DateFormat';
import { TextVariant } from '../../types/TextVariant';
import { Text } from '../Text/Text';

interface Props {
  /** JS Date */
  date?: Date;
  /** Date format */
  format?: DateFormat;
  /** Text variant */
  variant?: TextVariant;
  /** Enable tick */
  enableTick?: boolean;
}

export const DateText = memo(
  ({
    date: initialDate,
    format: initialFormat,
    variant = 'body',
    enableTick = false,
  }: Props) => {
    const [date, setDate] = useState(initialDate);
    const formatDate = useFormatDate();
    const hasLocalization = useFlag(FlagType.LanguageSelect);

    useEffect(() => {
      if (date !== initialDate) {
        setDate(initialDate);
      }
    }, [initialDate]);

    useEffect(() => {
      if (enableTick) {
        const intervalId = setInterval(() => {
          setDate(initialDate);
        }, ONE_MINUTE_INTERVAL);

        return () => clearInterval(intervalId);
      }
    }, [enableTick]);

    const format =
      hasLocalization && !initialFormat
        ? ('short' as const)
        : hasLocalization && initialFormat === 'iso'
          ? ('short' as const)
          : !hasLocalization && !initialFormat
            ? ('iso' as const)
            : initialFormat;

    const dateText = formatDate(date, format);

    return (
      <Text color="unset" variant={variant}>
        {dateText}
      </Text>
    );
  },
);
